// 走细粒度入口而不是包 barrel。两个理由:
// 1. i18n / 错误呈现是全 app 最早初始化的模块之一,从 barrel 进会把
//    resource-shell / wizard 那些 React 组件一起拖进来;
// 2. 有四个测试文件 `vi.mock('@crab/frontend-ui')` 整包替身 —— barrel 导入会让
//    它们因为缺一个从没听说过的导出而红。
// 路径必须与磁盘文件名逐字一致:四个 app 的 vite alias 与 tsconfig paths 都是
// 前缀替换到 `packages/crab-frontend-ui/src/*`,package.json 的 exports 表在这条
// 链路上根本不参与解析(所以也不必为它单独登记一条)。
import {
  isFormValidationError,
  renderFieldValidationError,
} from '@crab/frontend-ui/form/validationError';

export type Locale = 'es' | 'en' | 'zh';

export const SUPPORTED_LOCALES: Locale[] = ['es', 'en', 'zh'];
export const DEFAULT_LOCALE: Locale = 'es';
export const LANG_LABELS: Record<Locale, string> = { es: 'ES', en: 'EN', zh: '中文' };

const STORAGE_KEY = 'redcoral-lang';

let currentLocale: Locale = DEFAULT_LOCALE;
let isReady = false;
const readySubscribers = new Set<() => void>();
const translations: Record<Locale, Record<string, string>> = { es: {}, en: {}, zh: {} };
const subscribers = new Set<(locale: Locale) => void>();

function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        Object.assign(result, flattenObject(value as Record<string, unknown>, newKey));
      } else if (typeof value === 'string') {
        result[newKey] = value;
      }
    }
  }
  return result;
}

async function loadTranslations(): Promise<void> {
  try {
    const [esModule, enModule, zhModule] = await Promise.all([
      import('./locales/es.json'),
      import('./locales/en.json'),
      import('./locales/zh.json'),
    ]);
    translations.es = flattenObject(esModule.default);
    translations.en = flattenObject(enModule.default);
    translations.zh = flattenObject(zhModule.default);
  } catch (error) {
    console.error('[i18n] Failed to load translations:', error);
  }
}

function detectLocale(): Locale {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) return stored as Locale;
  const browserLang = navigator.language.slice(0, 2);
  if (SUPPORTED_LOCALES.includes(browserLang as Locale)) return browserLang as Locale;
  return DEFAULT_LOCALE;
}

export function getLocale(): Locale { return currentLocale; }

export function onI18nReady(callback: () => void): () => void {
  if (isReady) {
    callback();
  } else {
    readySubscribers.add(callback);
  }
  return () => readySubscribers.delete(callback);
}

export function load(): Promise<void> {
  return loadTranslations();
}

export function isI18nReady(): boolean {
  return isReady;
}

export function setLocale(locale: Locale): void {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  currentLocale = locale;
  window.localStorage.setItem(STORAGE_KEY, locale);
  document.documentElement.lang = locale;
  subscribers.forEach(cb => cb(currentLocale));
}

export function subscribeLocale(callback: (locale: Locale) => void): () => void {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

export function t(key: string, params?: Record<string, string | number>): string {
  let result = translations[currentLocale][key];
  if (result === undefined) result = translations[DEFAULT_LOCALE][key];
  if (result === undefined) {
    console.warn(`[i18n] Missing translation: "${key}"`);
    return key;
  }
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      // Tolerate both `{key}` and `{{key}}` placeholders. zh.json has 30+ keys
      // using `{{var}}` (e.g. remoteMenuTitle, categoryItemCount, pageIndicator).
      result = result.replace(new RegExp(`\\{\\{?${k}\\}\\}?`, 'g'), String(v));
    });
  }
  return result;
}

export function apiErrorMessage(
  translate: (key: string, params?: Record<string, string | number>) => string,
  code: number | undefined,
  _fallback: string,
  httpStatus?: number,
  /**
   * 后端 `AppError::with_detail` 带上来的结构化 detail。传了它,i18n 模板里的
   * 占位符才能被填 —— 例如 `"6202": "编号 {external_id} 已被「{occupied_by}」使用"`,
   * 读起来是一句人话,而不是一句「商品外部编号已存在。」让用户自己翻菜单找。
   *
   * 可选参数:现有调用点不传,行为与从前完全一致。
   */
  details?: Record<string, unknown> | null,
): string {
  if (code !== undefined) {
    const key = `error.${code}`;
    const params = details
      ? (Object.fromEntries(
          Object.entries(details).filter(
            ([, v]) => typeof v === 'string' || typeof v === 'number',
          ),
        ) as Record<string, string | number>)
      : undefined;
    const msg = translate(key, params);
    if (msg !== key) return msg;
  }
  if (httpStatus) {
    const key = `httpStatus.${httpStatus}`;
    const msg = translate(key);
    if (msg !== key) return msg;
  }
  const generic = translate('error.1');
  return generic !== 'error.1' ? generic : 'Error';
}

export function errorMessage(
  translate: (key: string, params?: Record<string, string | number>) => string,
  error: unknown,
  fallbackKey = 'auth.error_generic',
): string {
  // 表单值域校验自带 i18n 身份 —— 不能让它掉进下面的 fallbackKey。
  // 从前 payload builder 抛的是裸 `Error`,几十条不同的校验失败在用户眼里
  // 塌缩成同一句通用话(「操作失败」),不知道是哪个字段错了。
  if (isFormValidationError(error)) {
    return renderFieldValidationError(translate, error.validation);
  }

  if (typeof error === 'object' && error !== null) {
    const maybeApi = error as {
      code?: unknown;
      message?: unknown;
      status?: unknown;
      details?: unknown;
    };
    if (
      typeof maybeApi.message === 'string'
      && (typeof maybeApi.code === 'number' || typeof maybeApi.status === 'number')
    ) {
      return apiErrorMessage(
        translate,
        typeof maybeApi.code === 'number' ? maybeApi.code : undefined,
        maybeApi.message,
        typeof maybeApi.status === 'number' ? maybeApi.status : undefined,
        // 把后端的结构化 detail 一路带到 i18n 模板 —— 撞号才说得出「被谁占用」。
        typeof maybeApi.details === 'object'
          ? (maybeApi.details as Record<string, unknown> | null)
          : null,
      );
    }
  }
  return translate(fallbackKey);
}

/** Translate an enum-like value; missing enum translations are protocol/config errors. */
export function tEnum(prefix: string, value: string): string {
  const key = `${prefix}.${value}`;
  const result = translations[currentLocale][key] ?? translations[DEFAULT_LOCALE][key];
  if (result !== undefined) return result;

  const message = `[i18n] Missing enum translation: "${key}"`;
  if (import.meta.env.DEV) {
    throw new Error(message);
  }
  console.error(message);
  return t('common.label.unsupported_value');
}

export const i18n = {
  getLocale,
  setLocale,
  subscribe: subscribeLocale,
  t,
};

// Initialize: load translations, then detect locale and notify subscribers to trigger re-render
loadTranslations().then(() => {
  currentLocale = detectLocale();
  document.documentElement.lang = currentLocale;
  isReady = true;
  subscribers.forEach(cb => cb(currentLocale));
  readySubscribers.forEach(cb => cb());
  readySubscribers.clear();
});
